import { useForm } from '@tanstack/react-form'
import { useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { useCurrentBusiness } from '~/entities/business/model/use-business'
import { listingImagesApi } from '~/entities/listing/api'
import {
  useListing,
  usePublishListing,
  useUpdateListing,
} from '~/entities/listing/model/use-listings'
import {
  ListingContactSection,
  ListingDetailsSection,
  ListingHoursSection,
  ListingImagesSection,
  ListingLocationSection,
  ListingPreviewModal,
} from '~/features/listings/components'
import { GuardedActionButton } from '~/features/shared/components/guarded-action-button'
import type { ImageFile } from '~/features/shared/components/images-section'
import { useAfterSaveAction } from '~/features/shared/hooks/use-after-save-action'
import { saveStagedImages } from '~/features/shared/lib/image-order'
import * as m from '~/paraglide/messages'
import { getErrorMessageFromError } from '~/shared/lib/api-errors'
import { normalizeCountry } from '~/shared/lib/countries'
import { log } from '~/shared/lib/logger'
import { QueryKey, queryKey } from '~/shared/lib/query-keys'
import { storageManager } from '~/shared/lib/storage-manager'
import { useFormPersistence } from '~/shared/lib/use-form-persistence'
import { type Listing, toPriceLevelValue } from '~/shared/types'
import { Button } from '~/shared/ui/button'
import { Skeleton } from '~/shared/ui/skeleton'
import { PageHeader } from '~/widgets/page-header'

function buildListingFormValues(listing: Listing) {
  return {
    name: listing.name,
    description: listing.description,
    location: {
      name: listing.location.name || '',
      street: listing.location.street,
      city: listing.location.city,
      country: normalizeCountry(listing.location.country),
      coordinates: listing.location.coordinates,
    },
    postalCode: listing.postalCode ?? '',
    priceLevel: toPriceLevelValue(listing.priceLevel),
    phone: listing.phone || '',
    email: listing.email || '',
    website: listing.website || '',
    facebook: listing.facebook || '',
    instagram: listing.instagram || '',
    twitter: listing.twitter || '',
    nicknames: (listing.nicknames ?? []).join(', '),
    hours: listing.hours || [],
    tags: {
      mainCategoryId: listing.tags?.category?.id || '',
      subCategoryTags: listing.tags?.subCategories?.map((sub) => sub.id) || [],
      subSubCategoryTags: listing.tags?.subSubCategories?.map((subSub) => subSub.id) || [],
      amenityIds: listing.tags?.amenities?.map((amenity) => amenity.id) || [],
    },
  }
}

type ListingFormValues = ReturnType<typeof buildListingFormValues>

export const Route = createFileRoute('/dashboard/listings/$listingId/edit')({
  component: EditListing,
})

function EditListing() {
  const { listingId } = Route.useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { data: business } = useCurrentBusiness()

  useEffect(() => {
    queryClient.invalidateQueries({
      queryKey: queryKey(QueryKey.LISTING, listingId),
    })
  }, [listingId, queryClient])

  const { data: listing, isLoading } = useListing(business?.id || '', listingId)
  const updateListing = useUpdateListing(business?.id || '')
  const publishListing = usePublishListing(business?.id || '')
  const [images, setImages] = useState<ImageFile[]>([])
  const [primaryImageIndex, setPrimaryImageIndex] = useState(0)
  const [insertPositions, setInsertPositions] = useState<number[]>([])
  const [removedImageIds, setRemovedImageIds] = useState<string[]>([])
  const [isUploadingImages, setIsUploadingImages] = useState(false)

  const handleImagesChange = (
    newImages: ImageFile[],
    newPrimaryIndex: number,
    newInsertPositions?: number[],
  ) => {
    setImages(newImages)
    setPrimaryImageIndex(newPrimaryIndex)
    setInsertPositions(newInsertPositions || [])
  }

  const handleFormSubmit = async (
    value: ListingFormValues,
    clearFormState: () => void,
    afterSaveAction?: 'publish',
  ) => {
    try {
      await updateListing.mutateAsync({
        listingId,
        data: value,
      })

      if (images.length > 0) {
        setIsUploadingImages(true)
        try {
          await saveStagedImages(listingImagesApi, listingId, {
            existingIds: (listing?.images || []).map((img) => img.id),
            files: images.map((img) => img.file),
            insertPositions,
            removedIds: removedImageIds,
          })

          await queryClient.invalidateQueries({
            queryKey: queryKey(QueryKey.LISTING, listingId),
          })
          await queryClient.invalidateQueries({
            queryKey: queryKey(QueryKey.LISTINGS, business?.id),
          })

          toast.success(m.listingUpdatedWithImages(), {
            description: m.yourListingAndImagesHaveBeenUpdatedSuccessfully(),
          })
        } catch (imageError) {
          log.error(
            imageError instanceof Error ? imageError : new Error(String(imageError)),
            'Failed to upload images',
          )
          toast.warning(m.listingUpdatedButSomeImagesFailedToUpload(), {
            description: m.youCanAddImagesLaterFromTheListingDetailsPage(),
          })
        } finally {
          setIsUploadingImages(false)
        }
      } else {
        toast.success(m.listingUpdated(), {
          description: m.yourListingHasBeenUpdatedSuccessfully(),
        })
      }

      clearFormState()

      if (afterSaveAction === 'publish') {
        try {
          await publishListing.mutateAsync(listingId)
          toast.success(m.listingPublished())
        } catch (publishError) {
          toast.error(m.failedToPublishListing(), {
            description: getErrorMessageFromError(publishError),
          })
        }
      }

      navigate({ to: '/dashboard/listings/$listingId', params: { listingId } })
    } catch (error) {
      log.error(
        error instanceof Error ? error : new Error(String(error)),
        '[EditListing] Failed to update listing',
      )
      toast.error(getErrorMessageFromError(error))
    }
  }

  if (isLoading || !listing) {
    if (isLoading) {
      return <EditListingSkeleton />
    }
    return (
      <div className="space-y-6">
        <PageHeader
          title={m.listingNotFound()}
          description={m.theListingYoureLookingForDoesntExist()}
        />
      </div>
    )
  }

  return (
    <EditListingForm
      listing={listing}
      listingId={listingId}
      handleFormSubmit={handleFormSubmit}
      navigate={navigate}
      updateListing={updateListing}
      images={images}
      handleImagesChange={handleImagesChange}
      handleRemovedImagesChange={setRemovedImageIds}
      primaryImageIndex={primaryImageIndex}
      isUploadingImages={isUploadingImages}
    />
  )
}

interface EditListingFormProps {
  listing: Listing
  listingId: string
  handleFormSubmit: (
    value: ListingFormValues,
    clearFormState: () => void,
    afterSaveAction?: 'publish',
  ) => Promise<void>
  navigate: ReturnType<typeof useNavigate>
  updateListing: ReturnType<typeof useUpdateListing>
  images: ImageFile[]
  handleImagesChange: (
    newImages: ImageFile[],
    newPrimaryIndex: number,
    newInsertPositions?: number[],
  ) => void
  handleRemovedImagesChange: (imageIds: string[]) => void
  primaryImageIndex: number
  isUploadingImages: boolean
}

function EditListingForm({
  listing,
  listingId,
  handleFormSubmit,
  navigate,
  updateListing,
  images,
  handleImagesChange,
  handleRemovedImagesChange,
  primaryImageIndex,
  isUploadingImages,
}: EditListingFormProps) {
  const afterSave = useAfterSaveAction<'publish'>()
  const storageKey = `pulsify-edit-listing-${listingId}`
  storageManager.removeSession(storageKey)

  const form = useForm({
    defaultValues: buildListingFormValues(listing),
    onSubmit: async ({ value }) => {
      await handleFormSubmit(value, clearFormState, afterSave.consume() ?? undefined)
    },
  })

  const { clearFormState, hasSavedState } = useFormPersistence(form, {
    storageKey,
  })

  const handleCancel = () => {
    clearFormState()
    navigate({ to: '/dashboard/listings/$listingId', params: { listingId } })
  }

  const handleClearDraft = () => {
    form.reset(buildListingFormValues(listing))
    clearFormState()
    toast.info(m.changesDiscarded(), {
      description: m.yourUnsavedChangesHaveBeenDiscarded(),
    })
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={m.editListing()}
        description={m.updateYourListingInformation()}
        action={
          <div className="flex flex-wrap gap-2">
            <form.Subscribe
              selector={(state) => ({
                name: state.values.name,
                description: state.values.description,
                location: state.values.location,
                phone: state.values.phone,
              })}
            >
              {({ name, description, location, phone }) => (
                <ListingPreviewModal
                  name={name}
                  description={description}
                  location={location}
                  phone={phone}
                  coverImageUrl={
                    images.length > 0
                      ? images[primaryImageIndex]?.preview
                      : listing.images?.[0]?.url || listing.coverImageUrl || ''
                  }
                />
              )}
            </form.Subscribe>
            {hasSavedState() && (
              <Button variant="ghost" size="sm" onClick={handleClearDraft}>
                {m.discardChanges()}
              </Button>
            )}
            <Button variant="outline" onClick={handleCancel}>
              {m.cancel()}
            </Button>
          </div>
        }
      />
      <form
        onSubmit={async (e) => {
          e.preventDefault()
          e.stopPropagation()
          await form.handleSubmit()
        }}
      >
        <div className="mb-6 grid gap-6 lg:grid-cols-2 lg:items-start">
          <div className="min-w-0">
            <ListingDetailsSection form={form} />
          </div>
          <div className="min-w-0 space-y-6">
            <ListingImagesSection
              listingId={listingId}
              existingImages={listing.images || []}
              onImagesChange={handleImagesChange}
              onRemovedExistingChange={handleRemovedImagesChange}
            />
            <ListingLocationSection form={form} />
            <ListingContactSection form={form} />
          </div>
        </div>

        <div className="mb-6">
          <ListingHoursSection form={form} bufferKey={listingId} />
        </div>

        <form.Subscribe
          selector={(state) => ({
            canSubmit: state.canSubmit,
            isSubmitting: state.isSubmitting,
          })}
        >
          {({ canSubmit, isSubmitting }) => {
            const isDisabled =
              !canSubmit || updateListing.isPending || isUploadingImages || isSubmitting
            const isBusy = updateListing.isPending || isSubmitting
            const hasImages = !!(
              listing.coverImageUrl ||
              listing.images?.length > 0 ||
              images.length > 0
            )
            return (
              <div className="flex flex-wrap items-center gap-4">
                <Button type="submit" disabled={isDisabled}>
                  {isBusy && !afterSave.action
                    ? m.updating()
                    : isUploadingImages
                      ? m.uploadingImages()
                      : m.saveChanges()}
                </Button>
                {listing.status === 'Draft' && (
                  <GuardedActionButton
                    enabled={hasImages}
                    hint={m.addACoverImageToPublish()}
                    label={m.saveAndPublish()}
                    busyLabel={m.savingAndPublishing()}
                    busy={isBusy && afterSave.action === 'publish'}
                    disabled={isDisabled}
                    variant="secondary"
                    onClick={() => afterSave.trigger(form, 'publish')}
                  />
                )}
                <Button
                  type="button"
                  variant="outline"
                  className="sm:ml-auto"
                  onClick={handleCancel}
                >
                  {m.cancel()}
                </Button>
              </div>
            )
          }}
        </form.Subscribe>
      </form>
    </div>
  )
}

function EditListingSkeleton() {
  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="space-y-2">
          <Skeleton className="h-8 w-48 max-w-full" />
          <Skeleton className="h-4 w-96 max-w-full" />
        </div>
        <Skeleton className="h-10 w-24" />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="space-y-4 rounded-lg border p-6">
            <Skeleton className="h-6 w-32" />
            <Skeleton className="h-4 w-48" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ))}
      </div>

      <div className="space-y-4 rounded-lg border p-6">
        <Skeleton className="h-6 w-32" />
        <Skeleton className="h-32 w-full" />
      </div>

      <div className="flex flex-wrap gap-4">
        <Skeleton className="h-10 w-32" />
        <Skeleton className="h-10 w-24" />
      </div>
    </div>
  )
}
